import { setupCache } from 'axios-cache-adapter';
import localforage from 'localforage';
import { isNullOrUndefined } from 'utils/utils';

/**
 * Gets the default store config for the api Axios instance.
 */
export const getDefaultStore = () => localforage.createInstance({
  driver: localforage.LOCALSTORAGE,
  name: 'sg',
});

/**
 * Gets the default cache config for the api Axios instance.
 */
export const getDefaultCache = () => setupCache({
  maxAge: 1000 * 60 * 60, // Default cache to 1 hour (same as session duration)
  exclude: { query: false },
  store: getDefaultStore(),
});

const isValidParam = (value: any) => {
  if (typeof value !== 'number') {
    return !isNullOrUndefined(value) && value !== '';
  }
  // eslint-disable-next-line no-restricted-globals
  return !isNaN(value);
};

/**
 * Returns a new object where only params that are defined will be included.
 *
 * Excludes values that are:
 * - undefined
 * - null
 * - NaN
 * @param params
 */
export const createParams = (params: { [key: string]: any }) => {
  const p: any = {};
  Object.entries(params).forEach(([key, value]) => {
    if (isValidParam(value)) {
      p[key] = value;
    }
  });
  return p;
};

/**
 * Clears cached entries related to a company's details.
 * This is intentionally defensive and will swallow underlying storage errors
 * while logging warnings so callers don't fail because cache clearing failed.
 */
export const clearCompanyDetailsCache = async (companyId: number): Promise<void> => {
  try {
    const store = getDefaultStore();
    const allKeys = await store.keys();
    const companyKeys = allKeys.filter(key =>
      key.includes(`company/${companyId}`) ||
      key.includes(`company%2F${companyId}`) ||
      key.includes(encodeURIComponent(`company/${companyId}`))
    );

    for (const key of companyKeys) {
      try {
        await store.removeItem(key);
      } catch (error) {
        console.warn('Failed to clear cache key:', key, error);
      }
    }

    // Also attempt a couple of common direct key patterns
    const directKeys = [
      `company/${companyId}`,
      `company/${companyId}/detail`,
    ];

    for (const key of directKeys) {
      try {
        await store.removeItem(key);
      } catch (error) {
        // ignore
      }
    }
  } catch (error) {
    console.warn('Failed to clear company cache:', error);
  }
};

export const clearFlockCache = async (breedFlockCode: string): Promise<void> => {
  const trimmedCode = (breedFlockCode ?? '').trim();
  if (!trimmedCode) {
    return;
  }

  try {
    const store = getDefaultStore();
    const allKeys = await store.keys();
    const encodedCode = encodeURIComponent(trimmedCode);
    const patterns = [
      `flock/flock/${trimmedCode}`,
      `flock%2Fflock%2F${encodedCode}`,
      encodeURIComponent(`flock/flock/${trimmedCode}`),
      `flock/${trimmedCode}`,
      `flock%2F${encodedCode}`,
      encodeURIComponent(`flock/${trimmedCode}`),
    ];

    const flockKeys = allKeys.filter((key) =>
      patterns.some((pattern) => pattern && key.includes(pattern))
    );

    for (const key of flockKeys) {
      try {
        await store.removeItem(key);
      } catch (error) {
        console.warn('Failed to clear cache key:', key, error);
      }
    }

    const directKeys = [
      `flock/flock/${trimmedCode}`,
      `flock/${trimmedCode}`,
    ];

    for (const key of directKeys) {
      try {
        await store.removeItem(key);
      } catch (error) {
        // ignore direct key errors
      }
    }
  } catch (error) {
    console.warn('Failed to clear flock cache:', error);
  }
};

/**
 * Clears cached entries related to a user's details.
 * This is intentionally defensive and will swallow underlying storage errors
 * while logging warnings so callers don't fail because cache clearing failed.
 */
export const clearUserDetailsCache = async (userId: number): Promise<void> => {
  try {
    const store = getDefaultStore();
    const allKeys = await store.keys();
    const userKeys = allKeys.filter(key =>
      key.includes(`user/${userId}`) ||
      key.includes(`user%2F${userId}`) ||
      key.includes(encodeURIComponent(`user/${userId}`))
    );

    for (const key of userKeys) {
      try {
        await store.removeItem(key);
      } catch (error) {
        console.warn('Failed to clear cache key:', key, error);
      }
    }

    // Also attempt a couple of common direct key patterns
    const directKeys = [
      `user/${userId}`,
      `user/${userId}/detail`,
      `user/${userId}/edit`,
    ];

    for (const key of directKeys) {
      try {
        await store.removeItem(key);
      } catch (error) {
        // ignore
      }
    }
  } catch (error) {
    console.warn('Failed to clear user cache:', error);
  }
};
